// package
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Eye, EyeOff, X } from 'lucide-react';
import toast from 'react-hot-toast';
// custom
import SearchableSelect from '@shared/components/ui/SearchableSelect';
import { getMonasteryList } from '../api/monasteryService';
import { getMascotList } from '../api/mascotService';
import { login, register, getViewerToken } from '../api/authService';
import CloseButton from '@/shared/components/CloseButton';
import { useLanguage } from '@shared/contexts/LanguageContext';

/**
 * 通用登入/註冊表單
 * 可嵌入任何頁面或 modal，不依賴 router navigate
 *
 * Props:
 *   mode        - 'login' | 'register'（預設 'login'）
 *   onSuccess   - 成功後 callback(user)，呼叫端自行決定跳頁或關閉 modal
 *   onSwitchMode - 點「切換到另一模式」時的 callback，若不傳則不顯示切換連結
 */
const AuthForm = ({ mode = 'login', onSuccess, onSwitchMode, onLoadingChange }) => {
    const isLogin = mode === 'login';
    const { t, currentLanguage } = useLanguage();

    const [loading, setLoading] = useState(false);
    const [showBirthday, setShowBirthday] = useState(false);
    const [monasteryList, setMonasteryList] = useState([]);
    const [mascotList, setMascotList] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        dharmaName: '',
        jingshe: null,
        birthday: '',
        rewardId: null
    });

    const [showEncouragementPopup, setShowEncouragementPopup] = useState(false);
    const [encouragementText, setEncouragementText] = useState('');
    const [resultUser, setResultUser] = useState(null);

    const navigate = useNavigate();

    const formRef = useRef(null);
    const nameInputRef = useRef(null);
    const dharmaNameInputRef = useRef(null);
    const birthdayInputRef = useRef(null);

    useEffect(() => {
        getViewerToken().then(() => getMonasteryList()).then(setMonasteryList).catch(() => { });
    }, [currentLanguage]);

    const FALLBACK_MASCOTS = [
        { reward_id: 1, name: t('register.mascotGoldChild'), image: '/image/mascot/NEW_goldbaby.png' },
        { reward_id: 5, name: t('register.mascotElephant'), image: '/image/mascot/NEW_Elephant.png' },
        { reward_id: 9, name: t('register.mascotLion'), image: '/image/mascot/NEW_Lion.png' },
        { reward_id: 13, name: t('register.mascotDeer'), image: '/image/mascot/NEW_Deer.png' },
    ];

    useEffect(() => {
        if (!isLogin) {
            getViewerToken()
                .then(() => getMascotList())
                .then((list) => {
                    const resolved = list.length > 0 ? list : FALLBACK_MASCOTS;
                    setMascotList(resolved);
                    setFormData(prev => prev.rewardId ? prev : { ...prev, rewardId: resolved[0].reward_id });
                })
                .catch(() => {
                    setMascotList(FALLBACK_MASCOTS);
                    setFormData(prev => prev.rewardId ? prev : { ...prev, rewardId: FALLBACK_MASCOTS[0].reward_id });
                });
        }
    }, [isLogin, currentLanguage]);

    // mode 切換時清空表單
    useEffect(() => {
        setFormData({ name: '', dharmaName: '', jingshe: null, birthday: '', rewardId: null });
    }, [mode]);

    const validateBirthday = (value) => {
        if (!/^\d*$/.test(value)) return false;
        if (value.length > 4) return false;
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.jingshe || !formData.birthday) {
            alert(t('auth.requiredFieldsAlert'));
            return;
        }
        const month = parseInt(formData.birthday.substring(0, 2));
        const day = parseInt(formData.birthday.substring(2, 4));
        if (formData.birthday.length !== 4 ||
            isNaN(month) || month < 1 || month > 12 ||
            isNaN(day) || day < 1 || day > 31) {
            alert(t('auth.birthdayFormatAlert'));
            return;
        }

        setLoading(true);
        onLoadingChange?.(true);
        try {
            const payload = {
                name: formData.name,
                dharmaName: formData.dharmaName,
                jingsheId: formData.jingshe.id,
                birthday: formData.birthday,
                rewardId: formData.rewardId
            };
            const result = isLogin ? await login(payload) : await register(payload);
            toast.success(isLogin ? t('login.successToast') : t('auth.successTitle'));

            if (!isLogin && result.encouragement) {
                setEncouragementText(result.encouragement);
                setResultUser(result.user);
                setShowEncouragementPopup(true);
            } else {
                onSuccess?.(result.user);
            }
        } catch (error) {
            const msg = (isLogin ? t('login.failureToast') : t('register.failureToast')).replace('{{message}}', error.message || t('auth.checkInput'));
            toast.error(msg);
            console.error(msg);
        } finally {
            setLoading(false);
            onLoadingChange?.(false);
        }
    };

    const fields = (
        <>
            {/* 精舍 */}
            <div className="min-h-[50px]">
                <SearchableSelect
                    options={monasteryList}
                    value={formData.jingshe}
                    onChange={(option) => {
                        setFormData(prev => ({ ...prev, jingshe: option }));
                        if (option) {
                            window.requestAnimationFrame(() => nameInputRef.current?.focus());
                        }
                    }}
                    prefix={<>{t('auth.monasteryLabel')}<span className="text-[#BC313D]">*</span>　</>}
                    placeholder={t('auth.selectPlaceholder')}
                    wrap={currentLanguage.code === 'en'}
                    className="h-full"
                />
            </div>

            {/* 姓名 */}
            <div className="w-full h-[50px] bg-white rounded-full flex items-center px-4 outline outline-1 outline-[rgba(23,23,23,0.10)] focus-within:outline-2 focus-within:outline-[rgba(23,23,23,0.60)]">
                <span className="text-[rgba(110,98,91,0.50)] text-[18px] font-light whitespace-nowrap" style={{ fontFamily: 'Noto Sans TC' }}>{t('auth.nameLabel')}</span>
                <span className="text-[#BC313D] text-[18px] font-light mr-3" style={{ fontFamily: 'Noto Sans TC' }}>*</span>
                <input
                    ref={nameInputRef}
                    type="text"
                    placeholder={t('auth.inputPlaceholder')}
                    className="flex-1 bg-transparent outline-none text-[var(--color-text-main)] text-[18px] font-light placeholder:text-[rgba(110,98,91,0.30)]"
                    style={{ fontFamily: 'Noto Sans TC' }}
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); dharmaNameInputRef.current?.focus(); }
                    }}
                />
            </div>

            {/* 法名 */}
            <div className="w-full h-[50px] bg-white rounded-full flex items-center px-4 outline outline-1 outline-[rgba(23,23,23,0.10)] focus-within:outline-2 focus-within:outline-[rgba(23,23,23,0.60)]">
                <span className="text-[rgba(110,98,91,0.50)] text-[18px] font-light mr-3 whitespace-nowrap" style={{ fontFamily: 'Noto Sans TC' }}>{t('auth.dharmaNameLabel')}</span>
                <input
                    ref={dharmaNameInputRef}
                    type="text"
                    placeholder={t('auth.inputPlaceholder')}
                    className="flex-1 bg-transparent outline-none text-[var(--color-text-main)] text-[18px] font-light placeholder:text-[rgba(110,98,91,0.30)]"
                    style={{ fontFamily: 'Noto Sans TC' }}
                    value={formData.dharmaName}
                    onChange={(e) => setFormData(prev => ({ ...prev, dharmaName: e.target.value }))}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); birthdayInputRef.current?.focus(); }
                    }}
                />
            </div>

            {/* 生日 */}
            <div className="w-full h-[50px] bg-white rounded-full flex items-center px-4 outline outline-1 outline-[rgba(23,23,23,0.10)] focus-within:outline-2 focus-within:outline-[rgba(23,23,23,0.60)]">
                <span className="text-[rgba(110,98,91,0.50)] text-[18px] font-light whitespace-nowrap" style={{ fontFamily: 'Noto Sans TC' }}>{t('auth.birthdayLabel')}</span>
                <span className="text-[#BC313D] text-[18px] font-light mr-3" style={{ fontFamily: 'Noto Sans TC' }}>*</span>
                <input
                    ref={birthdayInputRef}
                    type={showBirthday ? 'text' : 'password'}
                    inputMode="numeric"
                    placeholder={t('auth.birthdayPlaceholder')}
                    className="min-w-0 flex-1 bg-transparent outline-none text-[var(--color-text-main)] text-[18px] font-light placeholder:text-[rgba(110,98,91,0.30)]"
                    style={{ fontFamily: 'Noto Sans TC' }}
                    value={formData.birthday}
                    onChange={(e) => {
                        if (validateBirthday(e.target.value)) {
                            setFormData(prev => ({ ...prev, birthday: e.target.value }));
                        }
                    }}
                    maxLength={4}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); formRef.current?.requestSubmit(); }
                    }}
                />
                {formData.birthday.length > 0 && <button
                    type="button"
                    onClick={() => setShowBirthday((v) => !v)}
                    className="shrink-0 -mr-1 ml-1 flex h-9 w-9 items-center justify-center rounded-full text-[rgba(110,98,91,0.55)] outline-none transition-colors hover:text-[var(--color-text-main)] focus-visible:ring-2 focus-visible:ring-[rgba(23,23,23,0.25)]"
                    aria-label={showBirthday ? t('auth.hideBirthday') : t('auth.showBirthday')}
                >
                    {showBirthday ? <EyeOff className="h-[22px] w-[22px]" strokeWidth={1.75} aria-hidden /> : <Eye className="h-[22px] w-[22px]" strokeWidth={1.75} aria-hidden />}
                </button>}
            </div>
        </>
    );

    if (isLogin) {
        return (
            <div className="w-full flex flex-col items-center py-7 px-5">
                {/* Title */}
                <div className="flex flex-col items-center mb-6">
                    <img src="/image/logo.png" alt="logo" className="w-[82px] h-[100px] object-contain mb-1" />
                    <div className="text-[#004682] text-[35px] font-black leading-[56px] tracking-[0.37px] text-center" style={{ fontFamily: 'Noto Serif TC' }}>{t('login.heroTitle')}</div>
                    <div className="text-[#BC313D] text-[24px] font-bold leading-[30px] tracking-[0.35px] text-center" style={{ fontFamily: 'Noto Serif TC' }}>{t('login.heroSubtitle')}</div>
                </div>
                <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col gap-3 w-full">
                    {fields}
                    <button type="submit" disabled={loading}
                        className="w-full h-[44px] mt-3 bg-[#BC313D] rounded-full text-white text-[18px] font-medium flex items-center justify-center hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-70 cursor-pointer"
                        style={{ fontFamily: 'Noto Sans TC' }}>
                        {loading ? t('login.submitting') : t('login.submit')}
                    </button>
                    {onSwitchMode && (
                        <button type="button" onClick={onSwitchMode}
                            className="w-full h-[44px] mt-3 bg-[rgba(188,49,61,0.08)] rounded-full text-[#BC313D] text-[17px] font-normal flex items-center justify-center outline outline-1 outline-[rgba(188,49,61,0.30)] hover:opacity-80 active:scale-[0.98] transition-all cursor-pointer"
                            style={{ fontFamily: 'Noto Sans TC' }}>
                            {t('auth.register')}
                        </button>
                    )}
                </form>
            </div>
        );
    }

    return (
        <>
            <div className="w-full flex flex-col p-6">
                <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col">
                    {/* 左側雙線容器：標題 + 欄位 + 吉祥物 */}
                    <div className="flex">
                        {/* 左側線條 */}
                        <div className="relative flex shrink-0 mr-6">
                            <div className="w-px bg-[#6E625B] self-stretch"></div>
                            <div className="w-[6px] bg-[#BC313D] absolute left-[-6px] top-0" style={{ height: '80px' }}></div>
                        </div>

                        {/* 右側內容 */}
                        <div className="flex flex-col flex-1 min-w-0">
                            {/* 標題 */}
                            <div className="py-2" style={{ fontFamily: 'Noto Serif TC' }}>
                                <span className="text-[#171717] text-[30px] font-bold leading-[33px]">{t('auth.register')}</span><br />
                                <span className="text-[#BC313D] text-[30px] font-bold leading-[33px]">{t('register.titleMember')}</span>
                            </div>

                            {/* 欄位 */}
                            <div className="py-8 flex flex-col gap-2">
                                {fields}
                            </div>

                            {/* 吉祥物 */}
                            {mascotList.length > 0 && (
                                <div className="pb-8 flex flex-col gap-2">
                                    <span className="text-[#6E625B] text-[16px] font-light" style={{ fontFamily: 'Noto Sans TC' }}>{t('register.mascotLabel')}</span>
                                    <div className="flex justify-between items-start">
                                        {mascotList.map((mascot) => (
                                            <button
                                                key={mascot.reward_id}
                                                type="button"
                                                onClick={() => setFormData(prev => ({ ...prev, rewardId: mascot.reward_id }))}
                                                className="flex flex-col items-center gap-0.5 w-[72px]"
                                            >
                                                <div className={`w-[72px] h-[72px] flex-shrink-0 rounded-[8px] flex items-center justify-center transition-all ${formData.rewardId === mascot.reward_id
                                                    ? 'outline outline-[3px] outline-[#BC313D] bg-white/90'
                                                    : 'outline outline-1 outline-[rgba(23,23,23,0.10)]'
                                                    }`}>
                                                    {mascot.image && <img src={mascot.image} alt={mascot.name} className="w-full h-full object-contain" />}
                                                </div>
                                                <span className="text-[#6E625B] text-[13px] font-light text-center leading-tight" style={{ fontFamily: 'Noto Sans TC' }}>{mascot.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 按鈕（線外） */}
                    <div className="flex gap-2 pt-6 pb-2">
                        {onSwitchMode && (
                            <button type="button" onClick={onSwitchMode}
                                className="flex-1 h-[44px] bg-[rgba(188,49,61,0.08)] rounded-full text-[#BC313D] text-[17px] font-normal flex items-center justify-center outline outline-1 outline-[rgba(188,49,61,0.30)] hover:opacity-80 active:scale-[0.98] transition-all cursor-pointer"
                                style={{ fontFamily: 'Noto Sans TC' }}>
                                {t('register.cancel')}
                            </button>
                        )}
                        <button type="submit" disabled={loading}
                            className="flex-1 h-[44px] bg-[#BC313D] rounded-full text-white text-[18px] font-medium flex items-center justify-center hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-70 cursor-pointer"
                            style={{ fontFamily: 'Noto Sans TC' }}>
                            {loading ? t('register.submitting') : t('auth.register')}
                        </button>
                    </div>
                </form>
            </div>

            {showEncouragementPopup && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4"
                    onClick={() => {
                        setShowEncouragementPopup(false);
                        onSuccess?.(resultUser);
                        navigate('/landing', { replace: true });
                    }}
                >
                    <div
                        className="w-96 px-6 pt-14 pb-6 relative bg-white rounded-[20px] shadow-[0px_11px_23px_0px_rgba(0,0,0,0.10),_0px_42px_42px_0px_rgba(0,0,0,0.09),_0px_95px_57px_0px_rgba(0,0,0,0.05),_0px_168px_67px_0px_rgba(0,0,0,0.01),_0px_263px_74px_0px_rgba(0,0,0,0.00)] inline-flex flex-col justify-start items-center cursor-default"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="justify-start text-[#171717] text-xl font-medium font-['Noto_Sans_TC'] leading-8 tracking-tight">{t('auth.successTitle')}</div>
                        <div className="justify-start text-[#9B887B] text-base font-normal font-['Noto_Sans_TC'] leading-6 text-center whitespace-pre-wrap">{encouragementText || t('register.defaultEncouragement')}</div>
                        <div className="h-10 left-[151px] top-0 absolute inline-flex justify-center items-end gap-[5px]">
                            <div className="w-20 h-20 relative bg-white rounded-[999px] shadow-[0px_5px_10px_0px_rgba(55,84,170,0.20)] overflow-hidden flex items-center justify-center">
                                {(() => {
                                    const selectedMascot = mascotList.find(m => m.reward_id === formData.rewardId);
                                    return selectedMascot?.image ? (
                                        <img src={selectedMascot.image} alt="Success Mascot" className="w-full h-full object-contain bg-white" />
                                    ) : (
                                        <div className="w-20 h-20 left-0 top-0 absolute bg-white" />
                                    );
                                })()}
                            </div>
                        </div>

                        {/* Action button */}
                        <div
                            className="mt-6 self-stretch h-11 px-2 pt-0.5 bg-[#BC313D] rounded-[999px] flex justify-center items-center gap-1 cursor-pointer"
                            onClick={() => {
                                setShowEncouragementPopup(false);
                                onSuccess?.(resultUser);
                                navigate('/landing', { replace: true });
                            }}
                        >
                            <div className="pb-0.5 flex justify-center items-center">
                                <div className="text-center justify-start text-white text-lg font-medium font-['Noto_Sans_TC'] leading-7">{t('register.enterHome')}</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

AuthForm.propTypes = {
    mode: PropTypes.oneOf(['login', 'register']),
    onSuccess: PropTypes.func,
    onSwitchMode: PropTypes.func,
    onLoadingChange: PropTypes.func,
};

export default AuthForm;
